import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, CheckCircle, AlertCircle, ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import fr from '../i18n/locales/fr';

const API_URL = process.env.REACT_APP_API_URL || '';

// Use French translations for consistency with Login page (French platform)
const t = (key, params = {}) => {
  const keys = key.split('.');
  let value = fr;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  if (typeof value !== 'string') return key;
  return value.replace(/\{(\w+)\}/g, (match, param) => {
    return params[param] !== undefined ? params[param] : match;
  });
};

const MagicLinkModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError('');
      setSuccess(false);
      setLoading(false);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/v1/auth/magic-link/request`, { email });
      setSuccess(true);
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        setError('Connection error. Please try again.');
      } else if (err.response?.status === 429) {
        setError('Veuillez attendre 2 minutes avant de demander un nouveau lien.');
      } else if (err.response?.status === 403) {
        setError(err.response.data?.detail || 'This account cannot use this feature.');
      } else {
        // Always show success for security (prevent email enumeration)
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-[#0d0d0d] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 0 50px rgba(0, 200, 255, 0.1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            <h2 className="text-xl font-semibold text-white">
              {success ? 'Email sent' : 'Magic link'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            // Success State
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Check your inbox
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                Si un compte existe avec l'adresse <span className="text-white font-medium">{email}</span>,
                you will receive an instant login link.
              </p>
              <p className="text-gray-500 text-xs mb-6">
                The link expires in 15 minutes and can only be used once. Remember to check your spam folder.
              </p>
              <button
                onClick={onClose}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-xl transition-colors"
              >
                Back to login
              </button>
            </div>
          ) : (
            // Form State
            <>
              <p className="text-gray-400 text-sm mb-6">
                Log in without a password! Enter your email and receive an instant login link.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.emailAddressLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#111111] border border-gray-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-600
                               focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20
                               hover:border-gray-700 transition-all"
                      placeholder={t('auth.emailPlaceholder')}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-xl
                           transition-colors flex items-center justify-center gap-2
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Envoyer le lien magique</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
                <p className="text-cyan-400/80 text-xs text-center">
                  The link logs you in automatically without a password. Perfect for a quick and secure login.
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to login</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MagicLinkModal;
